
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InlineEdit from '../../../components/base/InlineEdit';

const STORAGE_KEY = 'lonetrs_past_projects';

const defaultProjects = [
  {
    id: 1,
    title: 'Luxury Home Relocation',
    type: 'Residential',
    location: 'Las Vegas to San Francisco',
    date: '2024-01',
    image: 'https://public.readdy.ai/ai/img_res/8f4a3bb28083dbfe88d81678bbd1dbd4.jpg',
    description: 'Complete relocation of a 5,000 sq ft luxury residence with high-end furniture and art collection.',
  },
  {
    id: 2,
    title: 'Corporate Office Move',
    type: 'Commercial',
    location: 'Henderson to Portland',
    date: '2024-02',
    image: 'https://readdy.ai/api/search-image?query=modern%20corporate%20office%20interior%20professional%20workspace%20desks%20computers%20office%20furniture%20business%20relocation%20clean%20organized%20environment%20professional%20photography%20simple%20background&width=800&height=600&seq=proj2&orientation=landscape',
    description: 'Seamless weekend relocation of 50-employee tech company with minimal downtime.',
  },
  {
    id: 3,
    title: 'Classic Car Transport',
    type: 'Vehicle',
    location: 'Mesquite to Seattle',
    date: '2024-01',
    image: 'https://readdy.ai/api/search-image?query=classic%20vintage%20car%20automobile%20enclosed%20transport%20trailer%20professional%20vehicle%20shipping%20automotive%20transportation%20clean%20simple%20background%20professional%20photography&width=800&height=600&seq=proj3&orientation=landscape',
    description: 'Enclosed transport of vintage 1967 Mustang with white-glove service.',
  },
  {
    id: 4,
    title: 'Construction Site Delivery',
    type: 'Building Materials',
    location: 'Las Vegas to Los Angeles',
    date: '2024-03',
    image: 'https://readdy.ai/api/search-image?query=construction%20building%20materials%20lumber%20steel%20beams%20supplies%20organized%20stacked%20professional%20delivery%20truck%20construction%20site%20clean%20simple%20background%20professional%20photography&width=600&height=800&seq=proj4&orientation=portrait',
    description: 'Multi-trip delivery of building materials for commercial construction project.',
  },
  {
    id: 5,
    title: 'Medical Equipment Transport',
    type: 'Commercial',
    location: 'Las Vegas to San Diego',
    date: '2024-02',
    image: 'https://readdy.ai/api/search-image?query=medical%20equipment%20hospital%20machinery%20professional%20healthcare%20devices%20clean%20modern%20medical%20facility%20specialized%20transport%20clean%20simple%20background%20professional%20photography&width=800&height=600&seq=proj5&orientation=landscape',
    description: 'Temperature-controlled transport of sensitive medical equipment for new clinic.',
  },
  {
    id: 6,
    title: 'Multi-Family Relocation',
    type: 'Residential',
    location: 'Henderson to Sacramento',
    date: '2024-03',
    image: 'https://readdy.ai/api/search-image?query=family%20home%20interior%20cozy%20living%20space%20furniture%20household%20items%20residential%20moving%20boxes%20organized%20professional%20photography%20clean%20simple%20background&width=600&height=800&seq=proj6&orientation=portrait',
    description: 'Coordinated move for three families relocating to the same neighborhood.',
  },
];

type Project = typeof defaultProjects[0];

export default function PastProjects() {
  const [projects, setProjects] = useState<Project[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultProjects;
    } catch { return defaultProjects; }
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editingImageId, setEditingImageId] = useState<number | null>(null);
  const [imageDraft, setImageDraft] = useState('');

  const save = (updated: Project[]) => {
    setProjects(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const updateField = (id: number, field: keyof Project, value: string) => {
    const updated = projects.map(p => p.id === id ? { ...p, [field]: value } : p);
    save(updated);
    if (selectedProject?.id === id) {
      setSelectedProject(prev => prev ? { ...prev, [field]: value } : prev);
    }
  };

  const addProject = () => {
    const newProject: Project = {
      id: Date.now(),
      title: 'New Project',
      type: 'Residential',
      location: 'Origin to Destination',
      date: new Date().toISOString().slice(0, 7),
      image: 'https://readdy.ai/api/search-image?query=professional%20moving%20transportation%20relocation%20service%20clean%20modern%20simple%20background&width=600&height=800&seq=projnew&orientation=portrait',
      description: 'Click to edit this project description.',
    };
    save([...projects, newProject]);
  };

  const deleteProject = (id: number) => {
    save(projects.filter(p => p.id !== id));
    if (selectedProject?.id === id) setSelectedProject(null);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-900 to-dark-800 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            PAST <span className="text-electric-blue">PROJECTS</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-4">
            A showcase of our successful transportation and relocation projects
          </p>
          <button
            onClick={addProject}
            className="px-5 py-2 bg-electric-blue/20 border border-electric-blue/50 text-electric-blue font-inter text-sm rounded-full hover:bg-electric-blue/30 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
            Add Project
          </button>
        </motion.div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="break-inside-avoid group/card"
            >
              <div className="relative overflow-hidden rounded-2xl cursor-pointer">
                <img
                  src={project.image}
                  alt={project.title}
                  onClick={() => setSelectedProject(project)}
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover/card:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent opacity-60 group-hover/card:opacity-90 transition-opacity duration-300 pointer-events-none" />

                {/* Edit / Delete overlay buttons */}
                <div className="absolute top-3 right-3 flex gap-2 z-10 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={e => { e.stopPropagation(); setImageDraft(project.image); setEditingImageId(project.id); }}
                    className="w-8 h-8 flex items-center justify-center bg-electric-blue/90 hover:bg-electric-blue text-dark-900 rounded-full text-xs cursor-pointer transition-all"
                    title="Change image"
                  >
                    <i className="ri-image-edit-line"></i>
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); deleteProject(project.id); }}
                    className="w-8 h-8 flex items-center justify-center bg-red-500/80 hover:bg-red-500 text-white rounded-full text-xs cursor-pointer transition-all"
                    title="Delete project"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>

                <div
                  className="absolute inset-0 p-6 flex flex-col justify-end opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-electric-blue/20 border border-electric-blue/50 rounded-full mb-3 w-fit">
                    <span className="font-inter text-electric-blue text-xs font-semibold">{project.type}</span>
                  </div>
                  <h3 className="font-orbitron text-xl font-bold text-white mb-2">{project.title}</h3>
                  <div className="flex items-center space-x-2 text-white/70 text-sm font-inter mb-2">
                    <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{project.location}</span>
                  </div>
                  <button className="flex items-center space-x-2 text-electric-blue font-inter text-sm font-semibold">
                    <span>View Details</span>
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </button>
                </div>
              </div>

              {/* Image URL editor */}
              {editingImageId === project.id && (
                <div className="mt-2 p-3 bg-dark-800 border border-electric-blue/30 rounded-xl">
                  <p className="font-inter text-white/70 text-xs mb-2">Paste image URL:</p>
                  <input
                    type="url"
                    value={imageDraft}
                    onChange={e => setImageDraft(e.target.value)}
                    placeholder="https://..."
                    className="w-full bg-white/10 border border-electric-blue/50 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-electric-blue mb-2"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { updateField(project.id, 'image', imageDraft); setEditingImageId(null); }}
                      className="bg-electric-blue text-dark-900 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap"
                    >Save</button>
                    <button
                      onClick={() => setEditingImageId(null)}
                      className="bg-white/10 text-white px-3 py-1 rounded-full text-xs cursor-pointer whitespace-nowrap"
                    >Cancel</button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
            className="fixed inset-0 bg-dark-900/95 backdrop-blur-xl z-50 flex items-center justify-center p-6 cursor-pointer"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto cursor-default"
            >
              <div className="relative h-96 w-full">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-6 right-6 w-12 h-12 rounded-full bg-dark-900/80 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-electric-blue/20 hover:border-electric-blue transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="p-8">
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-electric-blue/20 border border-electric-blue/50 rounded-full mb-4">
                  <span className="font-inter text-electric-blue text-sm font-semibold">
                    <InlineEdit
                      value={selectedProject.type}
                      onSave={v => updateField(selectedProject.id, 'type', v)}
                      className="text-electric-blue"
                      placeholder="Project type..."
                    />
                  </span>
                </div>

                <h2 className="font-orbitron text-3xl font-bold text-white mb-4">
                  <InlineEdit
                    value={selectedProject.title}
                    onSave={v => updateField(selectedProject.id, 'title', v)}
                    className="text-white"
                    placeholder="Project title..."
                  />
                </h2>

                <div className="flex items-center space-x-6 mb-6">
                  <div className="flex items-center space-x-2 text-white/70 font-inter text-sm">
                    <i className="ri-map-pin-line text-electric-blue w-5 h-5 flex items-center justify-center"></i>
                    <InlineEdit
                      value={selectedProject.location}
                      onSave={v => updateField(selectedProject.id, 'location', v)}
                      className="text-white/70"
                      placeholder="Location..."
                    />
                  </div>
                  <div className="flex items-center space-x-2 text-white/70 font-inter text-sm">
                    <i className="ri-calendar-line text-electric-blue w-5 h-5 flex items-center justify-center"></i>
                    <InlineEdit
                      value={selectedProject.date}
                      onSave={v => updateField(selectedProject.id, 'date', v)}
                      className="text-white/70"
                      placeholder="YYYY-MM"
                    />
                  </div>
                </div>

                <p className="font-inter text-white/80 text-base leading-relaxed">
                  <InlineEdit
                    value={selectedProject.description}
                    onSave={v => updateField(selectedProject.id, 'description', v)}
                    multiline
                    rows={4}
                    className="text-white/80"
                    placeholder="Project description..."
                  />
                </p>

                <p className="font-inter text-electric-blue/60 text-xs mt-4 flex items-center gap-1">
                  <i className="ri-pencil-line w-3 h-3 flex items-center justify-center"></i>
                  Click any field above to edit it
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
